import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateEmployee } from '@/hooks/useEmployees'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { fr, arSA } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import { applyLanguageFallback } from '@/lib/language-fallback'

interface AddEmployeeDialogProps {
  restaurantId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function AddEmployeeDialog({
  restaurantId,
  open,
  onOpenChange,
  onSuccess,
}: AddEmployeeDialogProps) {
  const { t, i18n } = useTranslation()
  const createEmployeeMutation = useCreateEmployee(restaurantId)

  const isSubmittingRef = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [formData, setFormData] = useState({
    nameAr: '',
    nameFr: '',
    positionAr: '',
    positionFr: '',
    monthlySalary: 0,
    hireDate: new Date(),
  })

  const locale = i18n.language === 'ar' ? arSA : fr
  const isRTL = i18n.language === 'ar'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    setErrors({})

    const newErrors: Record<string, string> = {}
    if (!formData.nameAr && !formData.nameFr) {
      newErrors['nameAr'] = t('validation.required')
      newErrors['nameFr'] = t('validation.required')
    }
    if (!formData.monthlySalary || formData.monthlySalary <= 0) {
      newErrors['monthlySalary'] = t('validation.required')
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      isSubmittingRef.current = false
      setIsSubmitting(false)
      return
    }

    try {
      // Apply language fallback: if only one name is filled, use it for both
      const dataWithFallback = applyLanguageFallback({
        nameAr: formData.nameAr,
        nameFr: formData.nameFr,
      })

      const payload: {
        nameAr: string
        nameFr: string
        monthlySalary: number
        positionAr?: string
        positionFr?: string
        hireDate?: string
      } = {
        nameAr: dataWithFallback.nameAr!,
        nameFr: dataWithFallback.nameFr!,
        monthlySalary: formData.monthlySalary,
      }
      if (formData.positionAr) payload.positionAr = formData.positionAr
      if (formData.positionFr) payload.positionFr = formData.positionFr
      if (formData.hireDate) payload.hireDate = format(formData.hireDate, 'yyyy-MM-dd')

      await createEmployeeMutation.mutateAsync(payload)
      onOpenChange(false)
      setFormData({
        nameAr: '',
        nameFr: '',
        positionAr: '',
        positionFr: '',
        monthlySalary: 0,
        hireDate: new Date(),
      })
      onSuccess?.()
    } catch (error) {
      setErrors({ form: error instanceof Error ? error.message : t('error.generic') })
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] md:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('accounting.addEmployee')}</DialogTitle>
            <DialogDescription>{t('accounting.addEmployeeDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name AR */}
            <div className="space-y-2">
              <Label htmlFor="nameAr">{t('accounting.employeeName')} (AR)</Label>
              <Input
                id="nameAr"
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                className={errors['nameAr'] ? 'border-destructive' : ''}
                dir="rtl"
                placeholder={t('accounting.employeeNamePlaceholder')}
              />
              {errors['nameAr'] && (
                <p className="text-xs text-destructive">{errors['nameAr']}</p>
              )}
            </div>

            {/* Name FR */}
            <div className="space-y-2">
              <Label htmlFor="nameFr">{t('accounting.employeeName')} (FR)</Label>
              <Input
                id="nameFr"
                value={formData.nameFr}
                onChange={(e) => setFormData({ ...formData, nameFr: e.target.value })}
                className={errors['nameFr'] ? 'border-destructive' : ''}
                placeholder={t('accounting.employeeNamePlaceholder')}
              />
              {errors['nameFr'] && (
                <p className="text-xs text-destructive">{errors['nameFr']}</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              * {t('accounting.nameAtLeastOne')}
            </p>

            {/* Position AR */}
            <div className="space-y-2">
              <Label htmlFor="positionAr">{t('accounting.position')} (AR)</Label>
              <Input
                id="positionAr"
                value={formData.positionAr}
                onChange={(e) => setFormData({ ...formData, positionAr: e.target.value })}
                dir="rtl"
              />
            </div>

            {/* Position FR */}
            <div className="space-y-2">
              <Label htmlFor="positionFr">{t('accounting.position')} (FR)</Label>
              <Input
                id="positionFr"
                value={formData.positionFr}
                onChange={(e) => setFormData({ ...formData, positionFr: e.target.value })}
              />
            </div>

            {/* Monthly Salary */}
            <div className="space-y-2">
              <Label htmlFor="monthlySalary">{t('accounting.monthlySalary')} ({t('common.currency')}) *</Label>
              <Input
                id="monthlySalary"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.monthlySalary === 0 ? '' : formData.monthlySalary}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData({ ...formData, monthlySalary: isNaN(value) ? 0 : value });
                }}
                placeholder="0.00"
                className={errors['monthlySalary'] ? 'border-destructive' : ''}
              />
              {errors['monthlySalary'] && (
                <p className="text-xs text-destructive">{errors['monthlySalary']}</p>
              )}
            </div>

            {/* Hire Date */}
            <div className="space-y-2">
              <Label>{t('accounting.hireDate')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.hireDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                    {formData.hireDate ? (
                      format(formData.hireDate, 'PPP', { locale })
                    ) : (
                      <span>{t('common.selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.hireDate}
                    onSelect={(date) => date && setFormData({ ...formData, hireDate: date })}
                    initialFocus
                    locale={locale}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {errors['form'] && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
